/* eslint-disable react/jsx-no-duplicate-props */
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useForm, Controller } from 'react-hook-form';
import { faAirConditioner } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    DialogContent, InputAdornment, TextField,
    ToggleButton, ToggleButtonGroup
} from '@mui/material';

import {
    CustomTooltip, FlexContainer, FormAddress, FormLine, Question, RequiredSelectHack, SkeletonForm, TextError
} from 'components';
import { Datepicker } from 'components/Datepicker/Datepicker';
import { useStores } from 'hooks';
import { ModalProps } from 'hooks/ModalProvider';
import { CircuitView, RefrigerationUnitView } from 'models/detail/RefrigerationUnitView';
import { EquipmentsService } from 'services';
import shortid from 'shortid';
import { STOCK_ROLES_WITH_INTERVENTION_MANAGER } from 'utils/constants';
import {
    DocumentHelper, InterventionHelper,
    UserHelper,
    translate
} from 'utils/helpers';
import { SelectItem } from 'utils/types';
import { ModalHeader } from './ModalHeader';
import { CustomActionButtons } from './_CustomActionButtons';

export const AddEquipmentModal = ({
                                      onSubmit,
                                      onClose,
                                      defaultValues = new RefrigerationUnitView(),
                                      equipmentId,
                                      withPicture = false
                                  }: ModalProps<RefrigerationUnitView> & {
    defaultValues?: RefrigerationUnitView,
    equipmentId?: string,
    withPicture?: boolean
}) => {
    const { fluidStore, userStore } = useStores();
    const [uploadError, setUploadError] = useState('');
    const [picturesAdded, setPicturesAdded] = useState(defaultValues.documentList?.documents);
    const [isLoading, setIsLoading] = useState(false);
    const [installationTypes, setInstallationTypes] = useState<SelectItem[]>([]);
    const [yearError, setYearError] = useState<boolean>(false);

    // ✅ react-hook-form complet
    const {
        control,
        handleSubmit,
        setValue,
        watch,
        getValues,
        clearErrors,
        formState: { errors }
    } = useForm<RefrigerationUnitView>({
        defaultValues
    });

    const formValues = watch();
    const formattedfluidOptionsExtended = fluidStore.fluidExtendedOptions.map(fluid => ({
        value: fluid.id,
        label: fluid.name
    }));
    const checkYear = (year: string | number) => (Number(year) < 1990) || (Number(year) > new Date().getFullYear());
    const canValidate = equipmentId && !formValues.validated && UserHelper.hasAccessRight(STOCK_ROLES_WITH_INTERVENTION_MANAGER);

    useEffect(() => {
        if (!equipmentId) {
            return;
        }
        setIsLoading(true);
        const currentSetValue = setValue;
        const fluidOptions = [...fluidStore.fluidExtendedOptions].map(fluid => ({
            value: fluid.id,
            label: fluid.name
        }));

        EquipmentsService.getEquipmentDetail(equipmentId)
            .then((resp) => {
                if (resp.documentList && resp.documentList.documents) {
                    const pictures = resp.documentList.documents.map(document => ({
                        name: document.name,
                        base64Content: DocumentHelper.getDocumentWithBase64({ ...document, base64Content: document.content })
                    }));
                    setPicturesAdded(pictures);
                }
                Object.entries(resp).forEach(([key, value]) => {
                    currentSetValue(key as any, value);
                });
                const selectedFluid = fluidOptions.find(f => f.value === resp.circuits[0]?.fluid?.id);
                currentSetValue('fluid' as any, selectedFluid);
                currentSetValue('fluidWeight' as any, resp.circuits[0]?.fluidCapacity);

                if (resp.address && typeof resp.address !== 'string') {
                    currentSetValue('address1' as any, resp.address?.address1);
                    currentSetValue('address2' as any, resp.address?.address2);
                    currentSetValue('city' as any, resp.address?.city);
                    currentSetValue('country' as any, resp.address?.country);
                    currentSetValue('postalCode' as any, resp.address?.postalCode);
                }

                setYearError(resp.vehicleYear && checkYear(resp.vehicleYear));
            })
            .catch(error => {
                console.error('Error fetching equipment details:', error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [equipmentId, fluidStore, setValue]);

    useEffect(() => {
        if (userStore.isOffline) {
            InterventionHelper.loadInstallationTypesStored()
                .then(types => setInstallationTypes(types));
        } else {
            EquipmentsService.getInstallationTypes()
                .then(types => setInstallationTypes(types));
        }
    }, [userStore.isOffline]);
    const handleSelectFluid = (selectedFluid: SelectItem) => setValue('fluid', selectedFluid);
    const handleSelectInstallationType = (selectedType: SelectItem) => setValue('installationType', selectedType);

    const handleAddPicture = event => (
        DocumentHelper.handleFileUpload({ callback: setPicturesAdded, callbackError: setUploadError, event })
    );

    const handleYearChange = event => {
        event.preventDefault();
        const year: string = event.target.value;
        if (year === '') {
            setValue('vehicleYear', null);
            setYearError(false);
        } else if (/^\d+$/.test(year)) {
            setValue('vehicleYear', Number(year));
            setYearError(checkYear(year));
        }
    };

    const handleValidateModal = (data) => {
        if (formValues.installationType?.value === 'VEHICLE' && yearError) {
            return;
        }

        const selectedFluid = fluidStore.fluidExtendedOptions.find(fluid => fluid.id === formValues.fluid.value);

        const circuit: CircuitView = {
            ...(formValues.circuits && formValues.circuits[0]) ?? {},
            fluid: { ...selectedFluid },
            fluidAmount: Number(formValues.fluidWeight),
            fluidCapacity: Number(formValues.fluidWeight)
        };

        onSubmit({
            ...data,
            fluidWeight: Number(data.fluidWeight),
            circuits: [circuit],
            documentList: {
                documents: DocumentHelper.removeAllBase64Prefix(picturesAdded)
            },
            vehicleYear: data.vehicleYear && Number(data.vehicleYear),
            address: data.address1 ? {
                address1: data.address1,
                address2: data.address2,
                postalCode: data.postalCode,
                city: data.city,
                country: data.country
            } : null
        });
    };

    return (
        <>
            <form autoComplete="off" onSubmit={handleSubmit(handleValidateModal)}>
                <ModalHeader onClose={onClose}>
                    <FontAwesomeIcon icon={faAirConditioner} />
                    {equipmentId ? translate('modalEquipment.editEquipment') : translate('modalEquipment.newEquipment')}
                </ModalHeader>

                {isLoading ? (
                    <SkeletonForm />
                ) : (
                    <>
                        <DialogContent style={{ paddingTop: '10px' }} >
                            <FlexContainer flexDirection="column">
                                {equipmentId && (
                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        style={{ marginBottom: '2rem' }}
                                        value={formValues.activated}
                                        onChange={(event, value) => {
                                            if (value === null) return;
                                            setValue('activated', value);
                                        }}
                                    >
                                        <ToggleButton aria-label={translate('common.activated')} value>
                                            {translate('common.activated')}
                                        </ToggleButton>
                                        <ToggleButton aria-label={translate('common.deactivated')} value={false}>
                                            {translate('common.deactivated')}
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                )}
                                <Question label={translate('modalEquipment.fillFields')}>
                                    <Controller
                                        control={control}
                                        name="brand"
                                        render={({ field }) => (
                                            <TextField
                                                autoFocus
                                                error={!!errors.brand}
                                                fullWidth
                                                label={translate('common.brand')}
                                                {...field}
                                            />
                                        )}
                                    />
                                    <Controller
                                        control={control}
                                        name="model"
                                        render={({ field }) => (
                                            <TextField
                                                error={!!errors.model}
                                                label={translate('common.model')}
                                                required
                                                {...field}
                                            />
                                        )}
                                        rules={{ required: true }}
                                    />
                                    <Controller
                                        control={control}
                                        name="serialNumber"
                                        render={({ field }) => (
                                            <TextField
                                                error={!!errors.serialNumber}
                                                label={translate('common.serialNumber')}
                                                required
                                                {...field}
                                            />
                                        )}
                                        rules={{ required: true }}
                                    />
                                    {formattedfluidOptionsExtended && formattedfluidOptionsExtended.length > 0 && (
                                        <Question label={translate('common.fluidType')}>
                                            <Controller
                                                control={control}
                                                name="fluid"
                                                render={({ field }) => (
                                                    <Select
                                                        id="addEquipmentFluid"
                                                        isClearable
                                                        isSearchable
                                                        menuPortalTarget={document.body}
                                                        noOptionsMessage={() => translate('warnings.noOptionsAvailable')}
                                                        options={formattedfluidOptionsExtended}
                                                        placeholder={translate('common.searchByFluidType')}
                                                        styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                                                        {...field}
                                                        onChange={handleSelectFluid}
                                                    />
                                                )}
                                                rules={{ required: true }}
                                            />
                                            <RequiredSelectHack value={formValues.fluid || ''} />
                                        </Question>
                                    )}
                                    <Controller
                                        control={control}
                                        name="fluidWeight"
                                        render={({ field }) => (
                                            <TextField
                                                autoComplete="new-password"
                                                error={!!errors.fluidWeight}
                                                inputProps={{
                                                    step: '0.01',
                                                    min: 0
                                                }}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">{translate('common.weightUnit')}</InputAdornment>
                                                }}
                                                label={translate('common.fluidQuantity')}
                                                required
                                                type="number"
                                                {...field}
                                            />
                                        )}
                                        rules={{ required: true }}
                                    />
                                    <Question label={translate('interventionForm.control.hasLeakDetector')}>
                                        <ToggleButtonGroup
                                            aria-label={translate('interventionForm.control.hasLeakDetector')}
                                            exclusive
                                            size="medium"
                                            value={formValues.hasLeakDetectionSystem}
                                            onChange={(event, value) => {
                                                if (value === null) return;
                                                setValue('hasLeakDetectionSystem', value);
                                            }}
                                        >
                                            <ToggleButton aria-label={translate('common.yes')} value>
                                                {translate('common.yes')}
                                            </ToggleButton>
                                            <ToggleButton aria-label={translate('common.no')} value={false}>
                                                {translate('common.no')}
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Question>
                                    <FormLine>
                                        <Controller
                                            control={control}
                                            name="internalIdentification"
                                            render={({ field }) => (
                                                <TextField
                                                    autoComplete="new-password"
                                                    error={!!errors.internalIdentification}
                                                    label={translate('common.internalIdentification')}
                                                    {...field}
                                                />
                                            )}
                                        />
                                        <CustomTooltip size="sm" text={translate('common.searchByModelTruckNumber')} />
                                    </FormLine>
                                    <FormLine>
                                        <Datepicker
                                            disableFuture
                                            label="common.lastControlDate"
                                            name="lastControlDate"
                                            value={formValues.lastControlDate}
                                            onChange={value => setValue('lastControlDate', value)}
                                        />
                                    </FormLine>
                                    {installationTypes && installationTypes.length > 0 && (
                                        <Question label={translate('common.installationType')}>
                                            <Controller
                                                control={control}
                                                name="installationType"
                                                render={({ field }) => (
                                                    <Select
                                                        id="addInstallationType"
                                                        isClearable
                                                        isSearchable
                                                        menuPortalTarget={document.body}
                                                        noOptionsMessage={() => translate('warnings.noOptionsAvailable')}
                                                        options={installationTypes}
                                                        placeholder={translate('common.selectType')}
                                                        styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                                                        {...field}
                                                        onChange={handleSelectInstallationType}
                                                    />
                                                )}
                                            />
                                        </Question>
                                    )}
                                    {formValues.installationType?.value === 'VEHICLE' && (
                                        <>
                                            <FormLine>
                                                <Controller
                                                    control={control}
                                                    name="vehicleType"
                                                    render={({ field }) => (
                                                        <TextField
                                                            error={!!errors.vehicleType}
                                                            label={translate('common.vehicleType')}
                                                            {...field}
                                                        />
                                                    )}
                                                />
                                            </FormLine>
                                            <FormLine>
                                                <Controller
                                                    control={control}
                                                    name="vehicleBrand"
                                                    render={({ field }) => (
                                                        <TextField
                                                            error={!!errors.vehicleBrand}
                                                            label={translate('common.vehicleBrand')}
                                                            {...field}
                                                        />
                                                    )}
                                                />
                                            </FormLine>
                                            <FormLine>
                                                <Controller
                                                    control={control}
                                                    name="vehicleModel"
                                                    render={({ field }) => (
                                                        <TextField
                                                            error={!!errors.vehicleModel}
                                                            label={translate('common.vehicleModel')}
                                                            {...field}
                                                        />
                                                    )}
                                                />
                                            </FormLine>
                                            <FormLine>
                                                <TextField
                                                    autoComplete="new-password"
                                                    error={yearError}
                                                    inputProps={{
                                                        step: '1',
                                                        min: 1990
                                                    }}
                                                    label={translate('common.vehicleYear')}
                                                    type="text"
                                                    value={formValues.vehicleYear ?? ''}
                                                    onChange={e => handleYearChange(e)}
                                                />
                                            </FormLine>
                                            <FormLine>
                                                <Controller
                                                    control={control}
                                                    name="vehicleImat"
                                                    render={({ field }) => (
                                                        <TextField
                                                            error={!!errors.vehicleImat}
                                                            label={translate('common.vehicleImat')}
                                                            {...field}
                                                        />
                                                    )}
                                                />
                                            </FormLine>
                                            <FormLine>
                                                <Controller
                                                    control={control}
                                                    name="parcNumber"
                                                    render={({ field }) => (
                                                        <TextField
                                                            error={!!errors.parcNumber}
                                                            label={translate('common.parcNumber')}
                                                            {...field}
                                                        />
                                                    )}
                                                />
                                            </FormLine>
                                        </>
                                    )}
                                    {/* FormAddress */}
                                    <FormAddress
                                        control={control}
                                        getValues={getValues}
                                        isRequired={false}
                                        setValue={setValue}
                                        watch={watch}
                                        withCountry
                                    />
                                    <Controller
                                        control={control}
                                        name="comment"
                                        render={({ field }) => (
                                            <TextField
                                                label={translate('common.comment')}
                                                multiline
                                                {...field}
                                            />
                                        )}
                                    />
                                    {withPicture && (
                                        <Question label={translate('modalEquipment.equipmentPicture')}>
                                            <input
                                                accept="image/*"
                                                placeholder={translate('modalEquipment.equipmentPictureAdd')}
                                                type="file"
                                                onChange={handleAddPicture}
                                            />
                                            {uploadError && <TextError>{uploadError}</TextError>}
                                            {picturesAdded && picturesAdded.length > 0 && (
                                                <FlexContainer className="modalPicturesContainer">
                                                    {picturesAdded.map((picture) => {
                                                        if (picture.base64Content && picture.base64Content.includes('data:image')) {
                                                            return <img alt={picture.name} key={shortid.generate()} src={picture.base64Content} />;
                                                        }

                                                        return <img alt={picture.name} key={shortid.generate()} src={DocumentHelper.getImageWithBase64(picture)} />;
                                                    })}
                                                </FlexContainer>
                                            )}
                                        </Question>
                                    )}
                                </Question>
                            </FlexContainer>
                        </DialogContent>

                        <CustomActionButtons
                            forValidation={canValidate}
                            isLoading={isLoading}
                            onClose={onClose}
                        />
                    </>
                )}
            </form>
        </>
    );
};